/**
 * The email door into sign-up: an address and a password, and nothing else.
 *
 * Name, title and company all belong to the wizard — asking for them here would
 * rebuild the four-field form this split was meant to replace. What comes back
 * is an onboarding token, not a session: the account exists but owns nothing
 * until it has a workspace.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isAcceptablePassword } from '@/lib/password';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { setOnboardingToken, OnboardingError } from '@/lib/onboarding';
import { readPromoRef, rememberPromoRef } from '@/pages/Signup';
import { Alert, AlertDescription } from '@/components/shadcn/alert';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import { Logo } from '@/components/ui/Logo';

const schema = z.object({
  email: z.string().email(),
  /* The same rule the server enforces; see lib/password.ts. */
  password: z.string().min(8).refine(isAcceptablePassword),
});

type Values = z.infer<typeof schema>;

const COPY = {
  en: {
    title: 'Create your account',
    lede: 'Next you will set up your profile and your company.',
    email: 'Work email',
    password: 'Password',
    hint: 'At least 8 characters.',
    submit: 'Continue',
    back: 'Back',
    have: 'Already have an account?',
    login: 'Log in',
    taken: 'An account with this email already exists.',
    invalidEmail: 'Enter a valid email address.',
    shortPassword: 'At least 8 characters, including a letter and a number.',
    failed: 'Could not create your account. Try again.',
  },
  ar: {
    title: 'أنشئ حسابك',
    lede: 'بعدها ستُعِدّ ملفك الشخصي وشركتك.',
    email: 'بريد العمل',
    password: 'كلمة المرور',
    hint: '8 أحرف على الأقل.',
    submit: 'متابعة',
    back: 'رجوع',
    have: 'لديك حساب بالفعل؟',
    login: 'تسجيل الدخول',
    taken: 'يوجد حساب بهذا البريد بالفعل.',
    invalidEmail: 'أدخل بريدًا إلكترونيًا صحيحًا.',
    shortPassword: '٨ أحرف على الأقل، وتشمل حرفًا ورقمًا.',
    failed: 'تعذّر إنشاء الحساب. حاول مجددًا.',
  },
} as const;

interface RegisterResponse {
  onboarding: string;
}

export function SignupEmail() {
  const { lang } = useI18n();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const c = COPY[lang === 'ar' ? 'ar' : 'en'];

  /* Somebody can land straight on this page with the partner link's `?ref=`
     still attached, having skipped the door before it. */
  const ref = params.get('ref');
  useEffect(() => {
    rememberPromoRef(ref);
  }, [ref]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      /*
        The code travels with the account, not just with the browser.

        `/auth/register` writes it to `User.signup_promo_code`, which is the
        durable copy: the cookie set by the partner link does not survive the
        link being opened on a phone and the account being finished on a laptop,
        and the wizard between here and a created company is three screens long.
        Sent only when there is one. An empty string is not an optional field,
        it is a two-character minimum failing validation and losing the sign-up.
      */
      const carried = readPromoRef();
      const res = await fetch('/api/erp/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...values,
          locale: lang === 'ar' ? 'ar' : 'en',
          ...(carried ? { promo_code: carried } : {}),
        }),
      });
      const body = (await res.json().catch(() => ({}))) as RegisterResponse & {
        error?: string;
        code?: string;
      };
      if (!res.ok) {
        throw new OnboardingError(body.error ?? c.failed, res.status, body.code);
      }
      setOnboardingToken(body.onboarding);
      navigate('/onboarding', { replace: true });
    } catch (err) {
      if (err instanceof OnboardingError && err.code === 'EMAIL_TAKEN') setError(c.taken);
      else setError(err instanceof Error ? err.message : c.failed);
    }
  });

  return (
    <div className="flex min-h-svh flex-col items-center justify-center px-6 py-16">
      <div className="w-full max-w-[360px]">
        <div className="flex flex-col items-center text-center">
          <Logo size={32} markOnly />
          <h1 className="mt-6 text-2xl font-semibold tracking-tight">{c.title}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{c.lede}</p>
        </div>

        <form onSubmit={onSubmit} noValidate className="mt-8 space-y-4">
          {error ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="su-email">{c.email}</Label>
            <Input
              id="su-email"
              type="email"
              autoComplete="username"
              placeholder="you@company.com"
              autoFocus
              aria-invalid={errors.email ? true : undefined}
              {...register('email')}
            />
            {errors.email ? <p className="text-xs text-destructive">{c.invalidEmail}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="su-password">{c.password}</Label>
            <Input
              id="su-password"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              aria-invalid={errors.password ? true : undefined}
              {...register('password')}
            />
            <p className="text-xs text-muted-foreground">
              {errors.password ? (
                <span className="text-destructive">{c.shortPassword}</span>
              ) : (
                c.hint
              )}
            </p>
          </div>

          <Button type="submit" size="lg" className="h-11 w-full" disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : null}
            {c.submit}
            {!isSubmitting ? <ArrowRight className="size-4 rtl:rotate-180" /> : null}
          </Button>
        </form>

        <div className="mt-8 flex items-center justify-between text-sm">
          <Link
            to="/signup"
            className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3.5 rtl:rotate-180" />
            {c.back}
          </Link>
          <span className="text-muted-foreground">
            {c.have}{' '}
            <Link to="/login" className="font-medium text-foreground hover:underline">
              {c.login}
            </Link>
          </span>
        </div>
      </div>
    </div>
  );
}
